import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ContentWrap from '../../../ui/components/Layout/ContentWrap';
import { getLocalizedPath } from '../../app/pages/ui/utils/getLocalizedPath';
import type { IPressRelease } from '../../shared/api/contentful/types/IPressRelease';
import { useLocale } from '../../shared/i18n/locale/useLocale';
import { useTranslation } from '../../shared/i18n/translate/useTranslation';
import { PageLayout } from '../../shared/layout/page/PageLayout';
import { useNavigate } from '../../shared/router/useNavigate';
import { getSessionStorageItem } from '../../shared/utils/sessionStorage';
import { VisuallyHidden } from '../../../ui/components/VisuallyHidden/VisuallyHidden';
import {
	useAllYearsFilters,
	type YearFilterValue,
} from '../domain/hooks/useAllYearsFilters';
import { usePagination } from '../domain/hooks/usePagination';
import { useSearch } from '../domain/hooks/useSearch';
import type { IPressReleasesPageData } from '../domain/models/IPressReleasesPageData';
import { PageNavigation } from './components/PageNavigation';
import { PressReleaseList } from './components/press-release/PressReleaseList';
import { SearchBar } from './components/SearchBar';
import { StyledPageHeader } from './NewsStoriesPage';

export const PressReleasePage = ({
	page,
	pressReleases,
	pageNumber,
	itemsPerPage,
}: IPressReleasesPageData) => {
	const { t } = useTranslation();
	const yearsFilters: YearFilterValue[] = useAllYearsFilters(pressReleases);
	const [isHydrated, setIsHydrated] = useState(false);

	useEffect(() => {
		setIsHydrated(true);
	}, []);

	const initialYear = isHydrated
		? getSessionStorageItem('news-press-releases-year-filter') ||
		  yearsFilters[0]
		: yearsFilters[0];

	const { searchParams, setSearchParams, filterList } =
		useSearch<IPressRelease>(initialYear);

	const filteredPressReleases = useMemo(
		() => filterList(pressReleases),
		[searchParams, pressReleases],
	);

	const { currentPageItems, numberOfPages } = usePagination({
		list: filteredPressReleases,
		pageNumber,
		itemsPerPage,
	});

	const navigate = useNavigate();
	const locale = useLocale();

	const pressReleaseListRef = useRef<HTMLElement>(null);
	const isPaginatingRef = useRef(false);

	useEffect(() => {
		if (isPaginatingRef.current) {
			isPaginatingRef.current = false;
			pressReleaseListRef.current?.focus();
		}
	}, [pageNumber]);

	return (
		<PageLayout showAlertsDialog page={page}>
			<StyledPageHeader page={page} />
			<SearchBar
				years={yearsFilters}
				onSearch={async (value) => {
					await navigate(getLocalizedPath(`/news/press-releases/1`, locale));

					setSearchParams(value);
				}}
			/>
			<VisuallyHidden as="div" aria-live="polite" role="status">
				{t('poi_filter_results_count', {
					total: filteredPressReleases.length,
				})}
			</VisuallyHidden>
			<StyledContentWrap>
				<section
					ref={pressReleaseListRef}
					tabIndex={-1}
					aria-label={t('pagination_label', { pageNumber, numberOfPages })}
				>
					<PressReleaseList pressReleases={currentPageItems} />
				</section>
				{filteredPressReleases.length > 0 && (
					<PageNavigation
						pageNumber={pageNumber}
						numberOfPages={numberOfPages}
						onPageChange={() => {
							isPaginatingRef.current = true;
						}}
					/>
				)}
			</StyledContentWrap>
		</PageLayout>
	);
};

const StyledContentWrap = styled(ContentWrap)`
	display: flex;
	flex-direction: column;
	gap: 32px;
	margin-bottom: 80px;
`;
